import React, { useContext, useEffect, useState } from 'react';
import { ProductCodeKey, SubscriptionPlan, SubscriptionType } from '@dayetopia/library/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import CartItemPriceDisplay from '@components/cart/CartItemPriceDisplay';
import CartItemRow from '@components/cart/CartItemRow';
import CartItemImage from '@components/cart/ProductImage';
import RemoveFromCart from '@components/cart/RemoveFromCart';
import { Context } from '@components/Context';
import CartItemBoxItems from '@components/dashboard/CartItemBoxItems';
import Colors from '@components/shared/Colors';
import Select from '@components/shared/Select';
import { CartItem, ProductCode, SubscriptionCartItem } from '@contracts';
import { hasTamponsAndPads, getBoxItemsCount, convertBoxItems, getProvioticsBoxItems } from '@utils/cart';
import { showInCountryCondition } from '@utils/country';
import { isPadsAndStackBox } from '@utils/pads';
import { getProductPrice, getPriceText } from '@utils/price';
import { getProductPricesFromStorage } from '@utils/prices';

interface Props {
  item: SubscriptionCartItem;
  editable: boolean;
  subscriptionPlans: SubscriptionPlan[];
}

const SubscriptionCartItemRow: React.FC<Props> = ({ item, editable, subscriptionPlans }) => {
  const { t } = useTranslation();
  const { dispatch, state } = useContext(Context);
  const { country, currency } = state;
  const boxItems = convertBoxItems(item.boxItems, item.productCode, country);
  const boxItemsCount = getBoxItemsCount(boxItems);
  const [tamponPouchPrice, setTamponPouchPrice] = useState<number | undefined>();
  const [tamponTinPrice, setTamponTinPrice] = useState<number | undefined>();

  useEffect(() => {
    async function fetchPrices() {
      const pouchPrice = await getProductPrice(ProductCode.TamponPouch, currency, country);
      setTamponPouchPrice(pouchPrice.price);
      const tinPrice = await getProductPrice(ProductCode.TamponTin, currency, country);
      setTamponTinPrice(tinPrice.price);
    }
    fetchPrices();
  }, [currency, country]);

  const prepareDependantItems = (selectedPlan: SubscriptionPlan) => {
    const dependantItems: CartItem[] = [];
    if (selectedPlan.subscriptionType === SubscriptionType.Tampons) {
      const pouch = new CartItem('tampon_pouch_free', ProductCode.TamponPouch, 0);
      pouch.originalPrice = tamponPouchPrice;
      pouch.editable = false;
      dependantItems.push(pouch);
      if (selectedPlan.freeItem && selectedPlan.freeItem.key === 'free-case') {
        const tin = new CartItem('tin_case_free', ProductCode.TamponTin, 0);
        tin.originalPrice = tamponTinPrice;
        tin.editable = false;
        dependantItems.push(tin);
      }
    }
    return dependantItems;
  };

  const getSubscriptionLabel = (sp: SubscriptionPlan) => {
    if (sp.productCode === ProductCodeKey.ScreeningKit) {
      return t('components.subscription-options.tests-per-year', { count: sp.delivery.installments });
    }
    const discountPercent = sp.discountPercent && sp.discountPercent[country] ? sp.discountPercent[country] : sp?.discountPercent?.default;
    const discountPercentText = discountPercent ? `(${t('components.subscription.save', { percent: Math.round(discountPercent) })})` : '';
    return t(`components.subscription-options.subscription-title.${sp.planCode}`, {
      discountPercent: discountPercentText
    });
  };

  const getSelectOptions = () =>
    subscriptionPlans
      .filter((sp) => showInCountryCondition(sp.showInCountries, country))
      .map((sp) => ({
        label: getSubscriptionLabel(sp),
        value: sp
      }));
  const selectOptions = getSelectOptions();

  const onOptionSelect = (option: SubscriptionPlan) => {
    const newCartItem = {
      ...item,
      delivery: option.value.delivery,
      selectedPlan: option.value,
      price: option.value.billing.installmentPrice,
      dependantCartItems: prepareDependantItems(option.value)
    };

    // Set different box items on option select for Proviotics
    if (item.productCode === ProductCode.Proviotics) {
      newCartItem.boxItems = getProvioticsBoxItems(option.value);
    }

    dispatch({
      type: 'cartItemUpdate',
      data: newCartItem
    });
  };

  const getDefaultOptionValue = () => ({
    label: getSubscriptionLabel(item.selectedPlan),
    value: item.selectedPlan
  });

  const getImageVersion = (item: SubscriptionCartItem) => {
    if (hasTamponsAndPads(item)) {
      return 2;
    }
    return 1;
  };

  const getProductTitle = (item: SubscriptionCartItem) => {
    const pricesData = getProductPricesFromStorage();
    const product = pricesData && Array.isArray(pricesData) ? pricesData.find((priceItem) => priceItem.id === item.productCode) : null;

    if (item.productCode === ProductCode.Proviotics) {
      return t(`products.${item.selectedPlan.planCode}.title`);
    }

    if (product && product.name) {
      if (isPadsAndStackBox(item.productCode, country)) {
        return t('components.pads-plan');
      }
      return product.name;
    }

    if (hasTamponsAndPads(item)) {
      return t(`products.tampon_and_pads.title`);
    }

    return t('components.subscription-cart-item-title', {
      subscriptionType: t(`products.${item.productCode}.title`)
    });
  };

  const priceTextValue = () => getPriceText(item.price ?? 0, country, currency, t).fullString;

  return (
    <CartItemRow
      className="last-child-divider"
      imageComponent={<CartItemImage id={item.productCode} imageVersion={getImageVersion(item)} />}
      titleComponent={
        <TitleContainer>
          <Title>{getProductTitle(item)}</Title>
        </TitleContainer>
      }
      priceComponent={
        <CartItemPriceDisplay
          price={priceTextValue()}
          textColor={Colors.navyBlue}
          removeButtonComponent={editable ? <RemoveFromCart item={item} editable={editable} /> : undefined}
        />
      }
      extraContent={
        <ExtraContentContainer>
          {editable && selectOptions.length > 1 ? (
            <SelectContainer>
              <Select onSelectCallback={onOptionSelect} defaultValue={getDefaultOptionValue()} options={selectOptions} />
            </SelectContainer>
          ) : (
            <Subtitle>{getSubscriptionLabel(item.selectedPlan)}</Subtitle>
          )}
          {boxItemsCount > 1 && <CartItemBoxItems item={item} />}
        </ExtraContentContainer>
      }
    />
  );
};

export default SubscriptionCartItemRow;

const TitleContainer = styled.div`
  width: 100%;
`;

const Title = styled.h5`
  font-family: 'BrownStd';
  font-size: 14px;
  line-height: 22px;
  color: ${Colors.navyBlue};
`;

const Subtitle = styled.div`
  font-family: BrownStd;
  font-weight: normal;
  font-size: 13px;
  line-height: 22px;
  letter-spacing: 0;
  color: ${Colors.navyBlue};
`;

const ExtraContentContainer = styled.div`
  margin-top: 8px;
`;

const SelectContainer = styled.div`
  & .select-container {
    gap: 0;
  }
  & .select-options {
    position: relative;
  }
`;
